import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Paper,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MapIcon from '@mui/icons-material/Map';
import PublicIcon from '@mui/icons-material/Public';

const ExportRouteModal = ({ open, onClose, route, onExport }) => {
  const [loading, setLoading] = useState(false);

  const handleExport = async (format) => {
    try {
      setLoading(true);
      await onExport(route, format);
      setLoading(false);
      onClose();
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '16px', p: 2 },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Export Route
        </Typography>
        {!loading && (
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ textAlign: 'center', minHeight: 120 }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2, fontWeight: 500 }}>
              Exporting <b>{route?.name || 'route'}</b>...
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Please wait while we generate your file
            </Typography>
          </Box>
        ) : (
          <>
            <Typography sx={{ mb: 2, color: 'text.secondary' }}>
              Choose a format to export{' '}
              <b style={{ color: '#000' }}>{route?.name || 'this route'}</b>
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              {/* KMZ Option */}
              <Paper
                elevation={3}
                onClick={() => handleExport('kmz')}
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: 6,
                    bgcolor: '#f5f8ff',
                  },
                }}
              >
                <MapIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                <Typography sx={{ mt: 1, fontWeight: 500 }}>Export as KMZ</Typography>
              </Paper>

              {/* GeoJSON Option */}
              <Paper
                elevation={3}
                onClick={() => handleExport('geojson')}
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: 6,
                    bgcolor: '#f5f8ff',
                  },
                }}
              >
                <PublicIcon sx={{ fontSize: 40, color: '#388e3c' }} />
                <Typography sx={{ mt: 1, fontWeight: 500 }}>Export as GeoJSON</Typography>
              </Paper>
            </Box>
          </>
        )}
      </DialogContent>

      {/* Footer */}
      {!loading && (
        <DialogActions sx={{ justifyContent: 'center', pt: 2 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Select a format to start the download
          </Typography>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ExportRouteModal;
